import type {
	PresenceEvent,
	PresenceUser,
	WorkspaceMutationEvent,
} from "@apsu/shared";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router";

export type { PresenceUser, WorkspaceMutationEvent };

export type RealtimeStatus = "connecting" | "open" | "closed" | "reconnecting";

type RealtimeContextValue = {
	messages: WorkspaceMutationEvent[];
	presenceUsers: PresenceUser[];
	status: RealtimeStatus;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

const INITIAL_BACKOFF = 1000;
const MAX_BACKOFF = 30_000;
const HEARTBEAT_INTERVAL = 25_000;

export function RealtimeProvider({ children }: { children: ReactNode }) {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [messages, setMessages] = useState<WorkspaceMutationEvent[]>([]);
	const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
	const [status, setStatus] = useState<RealtimeStatus>("connecting");

	const wsRef = useRef<WebSocket | null>(null);
	const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const backoffRef = useRef(INITIAL_BACKOFF);

	const clearHeartbeat = useCallback(() => {
		if (heartbeatTimerRef.current) {
			clearInterval(heartbeatTimerRef.current);
			heartbeatTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (!workspaceSlug) return;

		const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
		const wsUrl = `${wsProtocol}//${window.location.host}/api/tenant/${workspaceSlug}/realtime`;

		function connect() {
			setStatus("connecting");
			const ws = new WebSocket(wsUrl);
			wsRef.current = ws;

			ws.onopen = () => {
				backoffRef.current = INITIAL_BACKOFF;
				setStatus("open");

				// Start heartbeat pings
				heartbeatTimerRef.current = setInterval(() => {
					if (ws.readyState === WebSocket.OPEN) {
						ws.send("ping");
					}
				}, HEARTBEAT_INTERVAL);
			};

			ws.onmessage = (e: MessageEvent) => {
				try {
					const data = JSON.parse(e.data as string) as
						| WorkspaceMutationEvent
						| PresenceEvent;

					if (data.type.startsWith("presence.")) {
						const presenceEvent = data as PresenceEvent;
						switch (presenceEvent.type) {
							case "presence.snapshot":
								setPresenceUsers(presenceEvent.payload.users);
								break;
							case "presence.joined":
								setPresenceUsers((prev) => [
									...prev,
									presenceEvent.payload.user,
								]);
								break;
							case "presence.left":
								setPresenceUsers((prev) =>
									prev.filter((u) => u.id !== presenceEvent.payload.user.id),
								);
								break;
						}
					} else {
						setMessages((prev) => [...prev, data].slice(-100));
					}
				} catch {
					// Ignore unparseable messages
				}
			};

			ws.onclose = () => {
				clearHeartbeat();
				setPresenceUsers([]);
				setStatus("reconnecting");
				scheduleReconnect();
			};

			ws.onerror = () => {
				// onclose will fire after onerror — reconnect is handled there
			};
		}

		function scheduleReconnect() {
			const delay = backoffRef.current;
			backoffRef.current = Math.min(delay * 2, MAX_BACKOFF);
			const jitteredDelay = delay * (0.7 + Math.random() * 0.3);
			reconnectTimerRef.current = setTimeout(connect, jitteredDelay);
		}

		connect();

		return () => {
			clearHeartbeat();
			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}
			if (wsRef.current) {
				wsRef.current.onclose = null;
				wsRef.current.close();
				wsRef.current = null;
			}
			setPresenceUsers([]);
			setStatus("closed");
		};
	}, [workspaceSlug, clearHeartbeat]);

	return (
		<RealtimeContext.Provider value={{ messages, presenceUsers, status }}>
			{children}
		</RealtimeContext.Provider>
	);
}

export function useRealtime() {
	const ctx = useContext(RealtimeContext);
	if (!ctx) throw new Error("useRealtime must be used within RealtimeProvider");
	return ctx;
}
